import {
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  InputRef,
  Button,
  Card,
  TableProps,
  Table,
  Flex,
  Typography,
  Tooltip,
  Modal,
  Form,
  Select,
  message,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "antd/es/form/Form";
import BatchBookingActions from "components/bookings/BatchBookingActions.component";
import BookingDetailDrawer from "components/bookings/BookingDetailDrawer.component";
import { showCompleteBookingConfirm } from "components/bookings/bookingConfirmModals";
import { buildCancelTypeOptions } from "components/bookings/cancelTypeOptions";
import { showConfirmModal } from "components/modals/confirmModal";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { AdminCapability } from "shared/utils/capabilities";
import PrimaryAction from "pages/schedule/component/PrimaryAction";
import {
  useUndoBookingByIdV2,
  useUpdateBookingByIdV2,
} from "hooks/mutations/booking-v2";
import { bookingsByScheduleV2Options } from "hooks/queries/booking-v2";
import { waitlistByScheduleV2Options } from "hooks/queries/waitlist-v2";
import { useEffect, useMemo, useRef, useState } from "react";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { tnum } from "shared/components/TableText";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { BookingDto, InactiveReason, Schedule, WindowConflict } from "shared/types";
import WindowConflictModal from "components/modals/WindowConflictModal.component";
import {
  AutoCompleteScope,
  BookingStatus,
  ClassCategory,
} from "shared/types/enum";
import {
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  getBookingStatusLabel,
  hasScheduleStarted,
  isSeatHoldingBookingStatus,
  isWaitlistStatus,
} from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate, now, tzDate } from "shared/utils/dayjs";
import { formatPhone } from "shared/utils/phone";
import { StatusBadge } from "./component/ScheduleStatusBadge";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import { textPx } from "shared/constants/zoom";

/**
 * Booking actions are time-independent: Complete / Cancel / No Show (and the
 * post-class Completed -> Refunded goodwill refund) can be set at any point in
 * the schedule's lifecycle, and any of them can be undone while the schedule
 * still exists. A deleted (tombstoned) schedule locks every booking — the
 * backend rejects changes and the UI disables them here.
 */

// Statuses whose last transition can be reversed, mapped to the previous
// status their undo edge restores. Mirrors the server's
// allowedBookingStatusTransitions undo edges (booking_v2.ts
// undoBookingTransition): Completed/Cancelled/NoShow restore to Confirmed,
// Refunded restores to Completed.
const UNDO_RESTORES_TO: Record<string, string> = {
  [BookingStatus.Completed]: BookingStatus.Confirmed,
  [BookingStatus.Cancelled]: BookingStatus.Confirmed,
  [BookingStatus.NoShow]: BookingStatus.Confirmed,
  [BookingStatus.Refunded]: BookingStatus.Completed,
};
const UNDOABLE_STATUSES: string[] = Object.keys(UNDO_RESTORES_TO);

// Status filter options, in the order a booking moves through them.
const STATUS_FILTER_ORDER: string[] = [
  BookingStatus.Pending,
  BookingStatus.Confirmed,
  BookingStatus.Completed,
  BookingStatus.NoShow,
  BookingStatus.Refunded,
  BookingStatus.Cancelled,
];

// The buttons a row WOULD offer at this status, rendered disabled. Used by
// both frozen cases (a deactivated client, and a comp on a voided sale) so
// they cannot drift apart: a frozen row keeps its shape in the column and
// simply cannot be pressed.
const frozenIconsFor = (booking: BookingDto) => {
  const status = booking.booking_status;
  return status === BookingStatus.Pending || status === BookingStatus.Confirmed
    ? [<CheckOutlined />, <CloseOutlined />]
    : status === BookingStatus.Completed && !booking.package_unlimited
      ? [<DollarOutlined />, <UndoOutlined />]
      : UNDOABLE_STATUSES.includes(status)
        ? [<UndoOutlined />]
        : [];
};

const ScheduleBooking = ({
  schedule,
  onWaitlistAdded,
  resumeCustomerId,
  onResumeConsumed,
  resumeBookingId,
  onResumeBookingConsumed,
}: {
  schedule: Schedule;
  onWaitlistAdded?: () => void;
  // Forwarded to PrimaryAction — see ScheduleDetails.tsx's resume effect.
  resumeCustomerId?: string;
  onResumeConsumed?: () => void;
  // Booking-ref search deep link (BookingRefSearch.component.tsx via
  // ScheduleDetails.tsx's resume effect) — opens this booking's detail
  // drawer once the schedule's bookings have loaded.
  resumeBookingId?: string;
  onResumeBookingConsumed?: () => void;
}) => {
  const schedule_id = schedule.schedule_id;
  const classType = schedule.class_type.type;
  const isPrivateSchedule = classType === ClassCategory.Private;
  // schedule.is_cancelled is the deleted (tombstone) marker.
  const scheduleLocked = schedule.is_cancelled;
  const hasStarted = hasScheduleStarted(schedule);
  const [form] = useForm();

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [promotedRowId, setPromotedRowId] = useState<string | null>(null);
  const [historyBooking, setHistoryBooking] = useState<BookingDto | null>(null);
  // Pembatasan Admin: the post-class refund, and undoing one (which moves
  // the same money back), can be Manager-only. Cancels are unaffected. A
  // restricted control is plainly disabled, no hover reason (Andrew,
  // 2026-09-03).
  const { isRestricted } = useAdminCapabilities();
  const refundRestricted = isRestricted(AdminCapability.BookingRefund);
  // An undo back to Confirmed on a class that has ended takes the booking out
  // of the auto-complete sweep for good (the server flags it), so where the
  // studio has auto-complete on for this class type (the sweep's own filter:
  // anything non-open counts as private) the undo pop-up says it stays
  // Confirmed until someone marks it.
  const { data: businessSettings } = useBusinessSettings();
  const autoCompleteCoversEndedClass = () => {
    const scope = businessSettings?.auto_complete_scope;
    return (
      !now().isBefore(tzDate(schedule.end_time)) &&
      !!businessSettings?.auto_complete_bookings &&
      (scope === AutoCompleteScope.All ||
        scope ===
          (classType === ClassCategory.Open
            ? AutoCompleteScope.Open
            : AutoCompleteScope.Private))
    );
  };
  const { data: schedules, isLoading } = useQuery(
    bookingsByScheduleV2Options(schedule_id, schedule.occurrence_timestamp),
  );
  const { data: waitlistEntries } = useQuery(
    waitlistByScheduleV2Options(schedule_id, schedule.occurrence_timestamp),
  );
  const { mutate: updateBooking } = useUpdateBookingByIdV2((promoted) => {
    message.success(
      translate("messages.success.auto_promoted").replace(
        "{{name}}",
        promoted.customer_name,
      ),
    );
    setPromotedRowId(promoted.id);
  });
  // An undo that would end a first-class package before a class already on
  // it: the refusal carries the conflict, and the admin's swap / extend
  // choice retries the undo with on_conflict.
  const [undoConflict, setUndoConflict] = useState<{
    conflict: WindowConflict;
    bookingId: string;
  } | null>(null);
  const { mutate: undoBooking } = useUndoBookingByIdV2((conflict, form) =>
    setUndoConflict({ conflict, bookingId: form.bookingId }),
  );

  useEffect(() => {
    if (!promotedRowId) return;
    const timer = setTimeout(() => setPromotedRowId(null), 2500);
    return () => clearTimeout(timer);
  }, [promotedRowId]);

  useEffect(() => {
    if (!resumeBookingId || !schedules) return;
    const match = schedules.find((b) => b.id === resumeBookingId);
    if (match) setHistoryBooking(match);
    onResumeBookingConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeBookingId, schedules]);

  const confirmedCount = (schedules ?? []).filter((b) =>
    isSeatHoldingBookingStatus(b.booking_status),
  ).length;
  // Per-schedule snapshot first, falling back to the live class max for
  // schedules created before capacity was snapshotted. Keeps the booking
  // screen's count in sync with the server's effectiveCapacity.
  const capacity = schedule.capacity ?? schedule.class_type.max_participants;
  // Cap check looks at the live queue only — expired rows are snapshot history.
  const waitlistLength = (waitlistEntries ?? []).filter(
    (e) => e.status === BookingStatus.Waitlist,
  ).length;

  // TODO: an `exclude_status` query param on getBookingsByScheduleId would let
  // us drop the client-side filter — keep here until that's wired up.
  // A to Z by client name whatever the status, so marking or cancelling a
  // booking never moves its row (the table used to group by status, and
  // every status change reshuffled the list). The Status column's filter
  // narrows to one status instead.
  const sortedBookings = useMemo(
    () =>
      [...(schedules ?? [])]
        .filter((b) => !isWaitlistStatus(b.booking_status))
        .sort(
          (a, b) =>
            (a.customer_name ?? "").localeCompare(b.customer_name ?? "") ||
            a.id.localeCompare(b.id),
        ),
    [schedules],
  );
  // The Status filter offers the statuses this class actually has, in the
  // order a booking moves through them.
  const statusFilters = STATUS_FILTER_ORDER.filter((status) =>
    sortedBookings.some((b) => b.booking_status === status),
  ).map((status) => ({ text: getBookingStatusLabel(status), value: status }));

  const getRowSelection = (
    selectedRowKeys: string[],
    setSelectedRowKeys: (keys: string[]) => void,
  ): TableProps<BookingDto>["rowSelection"] => ({
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys.map((key) => String(key)));
    },
    getCheckboxProps: (record: BookingDto) => ({
      disabled:
        scheduleLocked ||
        record.booking_status !== BookingStatus.Confirmed ||
        // A client deactivated at this studio is read-only (the server
        // refuses a batch that includes them).
        record.customer_active === false ||
        // Same for a comp: the sale this class was paid from was voided, so
        // the server freezes every status change on it and would refuse the
        // whole batch.
        record.package_voided === true,
      name: record.id,
    }),
  });

  const completeInfoModal = (bookingId: string) => {
    showCompleteBookingConfirm({
      onConfirm: () =>
        updateBooking({ bookingId, newStatus: BookingStatus.Completed }),
    });
  };

  const cancelWarningModal = (bookingId: string) => {
    showConfirmModal({
      tone: "amber",
      title: translate("messages.warning.cancel_booking.title"),
      destructive: true,
      confirmLabel: translate("button.cancel_booking"),
      // Disabled until a cancel type is chosen (it used to close with no
      // change and no message when left empty).
      requireChoice: { form, name: "cancelType" },
      content: (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="cancelType"
            required={true}
            label={translate("label.cancel_type")}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate(
                "label.cancel_type",
              )}`}
              options={buildCancelTypeOptions({
                cancelled: BookingStatus.Cancelled,
                noShow: BookingStatus.NoShow,
              })}
            />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("messages.warning.cancel_booking.content")}
          </Typography.Text>
        </Form>
      ),
      onConfirm: () => {
        const cancelType = form.getFieldValue("cancelType") as
          | BookingStatus
          | undefined;
        if (cancelType) {
          updateBooking({ bookingId, newStatus: cancelType });
        }
        form.resetFields();
      },
      onDismiss: () => form.resetFields(),
    });
  };

  // Post-class goodwill refund: Completed -> Refunded. Amber confirm that
  // spells out the consequence (credit returned, attendance kept) before
  // committing. Per-row only; there is deliberately no bulk refund in v1.
  // Names the package that receives the credit: the booking's own, or the
  // client's current one when the booking's package was upgraded since (the
  // server returns the credit there, System Review C2). An older server sends
  // no name and gets the original wording.
  const refundWarningModal = (booking: BookingDto) => {
    const packageName = booking.credit_package_name;
    showConfirmModal({
      tone: "amber",
      title: translate("messages.warning.refund_booking.title"),
      content: !packageName
        ? translate("messages.warning.refund_booking.content")
        : translateWithVars(
            booking.credit_package_upgraded
              ? "messages.warning.refund_booking.content_upgraded"
              : "messages.warning.refund_booking.content_package",
            { package: packageName },
          ),
      confirmLabel: translate("button.refund_booking"),
      onConfirm: () =>
        updateBooking({ bookingId: booking.id, newStatus: BookingStatus.Refunded }),
    });
  };

  // Pending requests are accepted (-> Confirmed) or rejected (-> Cancelled).
  // Both are irreversible, so we confirm before committing.
  const pendingDecisionModal = (bookingId: string, accept: boolean) => {
    Modal.confirm({
      title: accept
        ? translate("messages.warning.confirm_booking.title")
        : translate("messages.warning.reject_booking.title"),
      content: (
        <Typography>
          {accept
            ? translate("messages.warning.confirm_booking.content")
            : translate("messages.warning.reject_booking.content")}
        </Typography>
      ),
      okText: translate("button.confirm"),
      cancelText: translate("button.cancel"),
      maskClosable: true,
      onOk() {
        updateBooking({
          bookingId,
          newStatus: accept
            ? BookingStatus.Confirmed
            : BookingStatus.Cancelled,
        });
      },
    });
  };

  // A class taken on a package the client has since upgraded moves to their
  // current package for good when it goes back to Confirmed (the server's
  // undoBookingTransition), so that undo warns first and names the package.
  const undoInfoModal = (booking: BookingDto, movesPackage: boolean) => {
    showConfirmModal({
      tone: movesPackage ? "amber" : undefined,
      title: translate("messages.warning.undo_booking.title"),
      content: undoContent(booking, movesPackage),
      confirmLabel: translate("button.confirm"),
      onConfirm: () => undoBooking({ bookingId: booking.id }),
    });
  };

  // What the undo does to the credit, said for the move at hand (System
  // Review B22): undoing a cancel holds a credit again, undoing an attendance
  // mark turns the used credit back into a held one, undoing a refund takes
  // the returned credit back, and a membership moves no credit at all. Any
  // undo back to Confirmed after the class adds that the booking now stays
  // Confirmed (see autoCompleteCoversEndedClass).
  const undoContent = (booking: BookingDto, movesPackage: boolean) => {
    const restoresTo = UNDO_RESTORES_TO[booking.booking_status];
    const content = undoMoveContent(booking, movesPackage, restoresTo);
    return restoresTo === BookingStatus.Confirmed &&
      autoCompleteCoversEndedClass()
      ? `${content} ${translate("messages.warning.undo_booking.content_auto_complete")}`
      : content;
  };
  const undoMoveContent = (
    booking: BookingDto,
    movesPackage: boolean,
    restoresTo: string,
  ) => {
    const packageName =
      booking.credit_package_name ?? booking.customer_package_name;
    if (movesPackage && booking.credit_package_name) {
      return translateWithVars("messages.warning.undo_booking.content_upgraded", {
        package: booking.credit_package_name,
      });
    }
    if (booking.package_unlimited) {
      return translateWithVars("messages.warning.undo_booking.content_membership", {
        status: getBookingStatusLabel(restoresTo),
      });
    }
    if (!packageName) return translate("messages.warning.undo_booking.content");
    // The credits the booking took (its own snapshot), which the undo moves.
    const credits = booking.credit_cost ?? 1;
    const one = credits === 1 ? "_one" : "";
    if (booking.booking_status === BookingStatus.Cancelled) {
      return translateWithVars(`messages.warning.undo_booking.content_rehold${one}`, {
        n: credits,
        package: packageName,
      });
    }
    if (booking.booking_status === BookingStatus.Refunded) {
      return translateWithVars(`messages.warning.undo_booking.content_refund${one}`, {
        n: credits,
        package: packageName,
      });
    }
    return translate("messages.warning.undo_booking.content_unmark");
  };

  const searchInput = useRef<InputRef>(null);

  const renderActions = (booking: BookingDto) => {
    // Locked: schedule was deleted.
    if (scheduleLocked) {
      return (
        <Typography.Text type="secondary">
          {translate("label.locked")}
        </Typography.Text>
      );
    }

    // The sale this class was paid from was VOIDED, so the class is a
    // complimentary session: it stays on the record, and no credit may ever
    // move on or off it again. The server refuses every status change, so
    // the buttons show disabled WITH a reason (unlike the deactivated-client
    // case below, which the client page's banner already explains).
    if (booking.package_voided) {
      const icons = frozenIconsFor(booking);
      return icons.length ? (
        <Tooltip placement="topRight" title={translate("popover.booking_package_voided")}>
          <span>
            {icons.map((icon, i) => (
              <Button key={i} variant="link" icon={icon} disabled />
            ))}
          </span>
        </Tooltip>
      ) : null;
    }

    // A client deactivated at this studio is read-only: the same buttons,
    // plainly disabled with no hover reason (the client page's banner says
    // why, and the server refuses too).
    if (booking.customer_active === false) {
      const icons = frozenIconsFor(booking);
      return icons.length ? (
        <>
          {icons.map((icon, i) => (
            <Button key={i} variant="link" icon={icon} disabled />
          ))}
        </>
      ) : null;
    }

    if (booking.booking_status === BookingStatus.Pending) {
      return (
        <>
          <Tooltip placement="topRight" title={translate("popover.confirm_booking")}>
            <Button
              color="primary"
              variant="link"
              icon={<CheckOutlined />}
              onClick={() => pendingDecisionModal(booking.id, true)}
            />
          </Tooltip>
          <Tooltip placement="topRight" title={translate("popover.reject_booking")}>
            <Button
              color="danger"
              variant="link"
              icon={<CloseOutlined />}
              onClick={() => pendingDecisionModal(booking.id, false)}
            />
          </Tooltip>
        </>
      );
    }

    if (booking.booking_status === BookingStatus.Confirmed) {
      return (
        <>
          <Tooltip placement="topRight" title={translate("popover.complete_booking")}>
            <Button
              color="primary"
              variant="link"
              icon={<CheckOutlined />}
              onClick={() => completeInfoModal(booking.id)}
            />
          </Tooltip>
          <Tooltip placement="topRight" title={translate("popover.cancel_booking")}>
            <Button
              color="danger"
              variant="link"
              icon={<CloseOutlined />}
              onClick={() => cancelWarningModal(booking.id)}
            />
          </Tooltip>
        </>
      );
    }

    if (UNDOABLE_STATUSES.includes(booking.booking_status)) {
      // Mirror the server's undo guards so the button reflects them up front:
      //  - notUndoable: booking predates previous_status tracking (null) or
      //    its prior status doesn't match this row's undo edge (Completed/
      //    Cancelled/NoShow restore to Confirmed, Refunded to Completed) —
      //    the server rejects undo outright (booking_v2.ts
      //    undoBookingTransition), so disable rather than error
      //  - duplicate: the customer already holds another active seat here
      //  - capacity: restoring would exceed the class's effective capacity
      // The duplicate/capacity guards protect RE-OCCUPYING a seat, so (like
      // the server) they apply only when the undo restores a Confirmed
      // reservation; Refunded -> Completed reverses a post-class financial
      // decision and skips them.
      // Active rows other than this one (excluded so a Completed row being
      // undone isn't counted against itself — restoring it keeps its seat).
      const activeOthers = (schedules ?? []).filter(
        (b) => b.id !== booking.id && isSeatHoldingBookingStatus(b.booking_status),
      );
      const restoresTo = UNDO_RESTORES_TO[booking.booking_status];
      const notUndoable = booking.previous_status !== restoresTo;
      // Undo from Cancelled/Refunded re-takes a credit (re-hold / re-consume),
      // which an invalidated package (upgraded away, or a loyalty claw-back)
      // can no longer provide — the server refuses, so disable up front.
      // Strict === false: rows from an older server (field absent) keep
      // current behavior. Undos from Completed/NoShow only release credits.
      const takesCredit =
        booking.booking_status === BookingStatus.Cancelled ||
        booking.booking_status === BookingStatus.Refunded;
      // On a class booked on a package the client has since upgraded, a
      // restore to Confirmed moves the booking to the client's current
      // package for good (warned in undoInfoModal), and undoing a refund
      // takes the credit back from that package. Either way the current
      // package must still exist (System Review C2).
      const movesPackage =
        booking.credit_package_upgraded === true &&
        restoresTo === BookingStatus.Confirmed;
      const packageDeleted =
        booking.credit_package_upgraded === true &&
        (takesCredit || movesPackage) &&
        booking.credit_package_valid === false;
      const packageInvalid =
        takesCredit &&
        !booking.credit_package_upgraded &&
        booking.package_is_valid === false;
      // Undoing a cancel puts the booking back on its package, which must
      // still run on the class day (a first-class package re-anchored on an
      // earlier class or waitlist spot, or an end date moved earlier since
      // the cancel).
      const packageEnded = booking.package_ends_before_class === true;
      // The credits this booking released may have been spent on another
      // class since; the server refuses an undo that would overdraw the
      // package (the undo takes the booking's own credit_cost again).
      // null = unlimited, undefined = an older server.
      const noFreeCredit =
        takesCredit &&
        typeof booking.package_available_credits === "number" &&
        booking.package_available_credits < (booking.credit_cost ?? 1);
      const seatGuardsApply = restoresTo === BookingStatus.Confirmed;
      const duplicateActive =
        seatGuardsApply &&
        !!booking.customer_id &&
        activeOthers.some((b) => b.customer_id === booking.customer_id);
      const atCapacity = seatGuardsApply && activeOthers.length >= capacity;
      const refundLocked =
        refundRestricted && booking.booking_status === BookingStatus.Refunded;
      const undoDisabled =
        refundLocked ||
        notUndoable ||
        packageDeleted ||
        packageInvalid ||
        packageEnded ||
        noFreeCredit ||
        duplicateActive ||
        atCapacity;
      const undoTooltip = notUndoable
        ? translate("popover.undo_unavailable")
        : packageDeleted
          ? translate("popover.undo_package_deleted")
          : packageInvalid
          ? translate("popover.undo_package_invalid")
          : packageEnded
          ? translate("popover.undo_package_ended")
          : noFreeCredit
            ? translate("popover.undo_no_credit")
            : duplicateActive
              ? translate("popover.undo_duplicate")
              : atCapacity
                ? translate("popover.undo_full")
                : translate("popover.undo_booking");
      const undoTrigger = (
        // span wrapper so the tooltip still shows while the button is
        // disabled (antd suppresses pointer events on disabled buttons)
        <span style={{ display: "inline-block" }}>
          <Button
            variant="link"
            icon={<UndoOutlined />}
            disabled={undoDisabled}
            onClick={() => undoInfoModal(booking, movesPackage)}
          />
        </span>
      );
      // The refund restriction disables undoing a refund with no hover reason
      // at all; every other disabled state keeps explaining itself.
      const undoButton = refundLocked ? (
        undoTrigger
      ) : (
        <Tooltip placement="topRight" title={undoTooltip}>{undoTrigger}</Tooltip>
      );
      // Completed rows additionally offer the post-class goodwill refund
      // (per-row only; no bulk refund in v1).
      // A membership has no credit to return, so it has no Refund (System
      // Review B24; the server refuses it too).
      if (
        booking.booking_status === BookingStatus.Completed &&
        !booking.package_unlimited
      ) {
        // The credit has nowhere to go: the booking's package was upgraded
        // and the studio deleted the package it became (the server refuses).
        const refundNoPackage =
          booking.credit_package_upgraded === true &&
          booking.credit_package_valid === false;
        return (
          <>
            {refundRestricted ? (
              <Button variant="link" icon={<DollarOutlined />} disabled />
            ) : refundNoPackage ? (
              <Tooltip placement="topRight" title={translate("popover.refund_package_deleted")}>
                <span style={{ display: "inline-block" }}>
                  <Button variant="link" icon={<DollarOutlined />} disabled />
                </span>
              </Tooltip>
            ) : (
              <Tooltip placement="topRight" title={translate("popover.refund_booking")}>
                <Button
                  variant="link"
                  icon={<DollarOutlined />}
                  onClick={() => refundWarningModal(booking)}
                />
              </Tooltip>
            )}
            {undoButton}
          </>
        );
      }
      return undoButton;
    }

    return null;
  };

  const columns: TableProps<BookingDto>["columns"] = [
    {
      title: translate("label.booking_date"),
      dataIndex: "booking_date",
      key: "booking_date",
      render: (_, { booking_date }) =>
        `${formatDate(booking_date, DEFAULT_DATE_FORMAT + ", HH:mm")}`,
    },
    {
      title: translate("label.customer_name"),
      dataIndex: "customer_name",
      key: "customer_name",
      ...getColumnSearchProps({ config: "customer_name", searchInput }),
      // The rows already come A to Z; the arrow says so.
      sorter: (a, b) => (a.customer_name ?? "").localeCompare(b.customer_name ?? ""),
      defaultSortOrder: "ascend",
    },
    {
      title: translate("label.phone_number"),
      dataIndex: "customer_phone",
      key: "customer_phone",
      ...getColumnSearchProps<BookingDto, "customer_phone">({
        config: { dataIndex: "customer_phone", searchType: "phone" },
        searchInput,
      }),
      render: (_, { customer_phone }) => (
        <span style={tnum}>{formatPhone(customer_phone)}</span>
      ),
    },
    {
      title: translate("package"),
      dataIndex: "customer_package_name",
      key: "customer_package_name",
      // A comp's row would otherwise look exactly like a paid one: same
      // status, same attendee, nothing to say the class earned nothing. The
      // badge is the one place in a list this house rule allows, because the
      // difference is money and it is invisible otherwise.
      render: (name: string | undefined, row: BookingDto) => (
        <Flex align="center" gap={6} wrap="wrap">
          <span>{name}</span>
          {row.package_voided && (
            <StatusBadge
              tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
              label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
            />
          )}
        </Flex>
      ),
    },
    {
      // Credits the booking took; a membership takes none.
      title: translate("label.credits"),
      dataIndex: "credit_cost",
      key: "credit_cost",
      width: 90,
      render: (_, row: BookingDto) => (
        <span style={tnum}>
          {row.package_unlimited ? "-" : (row.credit_cost ?? 1)}
        </span>
      ),
    },
    {
      title: translate("label.booking_status"),
      dataIndex: "booking_status",
      key: "booking_status",
      filters: statusFilters,
      onFilter: (value, record) => record.booking_status === value,
      render: (_, { booking_status }) => (
        <StatusBadge
          tone={bookingStatusTone(booking_status)}
          label={getBookingStatusLabel(booking_status)}
        />
      ),
    },
    {
      title: translate("label.action"),
      key: "action",
      // Row actions (Complete/Cancel/Undo/Refund) must not also open the
      // detail drawer, so their clicks are stopped from bubbling up to the
      // row's own onClick (which opens the drawer — see the Table's onRow).
      render: (_, booking) => (
        <Flex onClick={(e) => e.stopPropagation()}>{renderActions(booking)}</Flex>
      ),
    },
  ];

  return (
    <Card>
      <Flex vertical gap={18}>
        <Flex justify="space-between" align="flex-start" gap={16}>
          <Flex vertical>
            <Typography.Title
              level={5}
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: textPx(17),
                letterSpacing: "-0.01em",
              }}
            >
              {translate("title.schedule_bookings")}
            </Typography.Title>
            <Typography.Text
              style={{ color: colors.ink2, fontSize: textPx(13), marginTop: 4 }}
            >
              {translate("subtitle.schedule_bookings")}
            </Typography.Text>
          </Flex>
          <PrimaryAction
            scheduleId={schedule_id}
            occurrenceTimestamp={schedule.occurrence_timestamp}
            classId={schedule.class_type.id}
            isPrivate={isPrivateSchedule}
            locked={scheduleLocked}
            hasStarted={hasStarted}
            confirmedCount={confirmedCount}
            capacity={capacity}
            waitlistLength={waitlistLength}
            onWaitlistAdded={onWaitlistAdded}
            resumeCustomerId={resumeCustomerId}
            onResumeConsumed={onResumeConsumed}
          />
        </Flex>
        <Table
          sticky
          rowKey={(record) => record.id}
          dataSource={sortedBookings}
          loading={isLoading}
          size={"middle"}
          columns={columns}
          style={{ borderRadius: 50 }}
          rowClassName={(record) =>
            record.id === promotedRowId ? "auto-promoted-row" : ""
          }
          // Clicking anywhere on a row opens its detail drawer (the action
          // column stops its own clicks from bubbling here — see the action
          // column's render above).
          onRow={(record) => ({
            onClick: () => setHistoryBooking(record),
            style: { cursor: "pointer" },
          })}
          rowSelection={{
            type: "checkbox",
            ...getRowSelection(selectedRowKeys, setSelectedRowKeys),
          }}
          footer={() => {
            return (
              <BatchBookingActions
                selectedRowKeys={selectedRowKeys}
                onSelectionClear={() => setSelectedRowKeys([])}
                completeDisabled={scheduleLocked}
                cancelDisabled={scheduleLocked}
              />
            );
          }}
        />
      </Flex>
      <BookingDetailDrawer
        booking={historyBooking}
        onClose={() => setHistoryBooking(null)}
      />
      <WindowConflictModal
        conflict={undoConflict?.conflict ?? null}
        onCancel={() => setUndoConflict(null)}
        onChoose={(choice) => {
          if (undoConflict) {
            undoBooking({ bookingId: undoConflict.bookingId, on_conflict: choice });
          }
          setUndoConflict(null);
        }}
      />
    </Card>
  );
};

export default ScheduleBooking;
